'use strict';

/**
 * Helpers for gender-condition compatibility checks on nutrition patterns.
 *
 * The authoritative source for whether a pattern is biologically female-only
 * is the backend-computed `female_only` boolean on the `/nutrition-patterns`
 * response. The server derives it from `templates[*].demographic.gender`
 * (see users/templates/nutrition-pattern/nutrition-pattern-base-1.0.php).
 * Today this covers Pregnancy, Lactation, Postpartum, Menstrual Phase,
 * Follicular & Ovulation Phase, Luteal Phase, Menopause, Post Menopause,
 * Fertility, Gestational Diabetes, and PCOS.
 */

/**
 * Checks whether a nutrition pattern is restricted to female patients.
 *
 * @param {Object} pattern - A nutrition pattern object from NutritionPatternStore.
 * @returns {boolean} True when the pattern is flagged as female-only.
 */
export function isFemaleOnlyCondition(pattern) {
    return !!(pattern && pattern.female_only);
}

/**
 * Resolves a condition reference to its full nutrition pattern.
 *
 * `conditions` state can be stored as plain strings (legacy callers) or as
 * `{name, uuid}` objects. This helper normalises both shapes against the
 * loaded `nutritionPatterns` collection so callers can reason about the
 * hydrated pattern (with fields like `female_only`).
 *
 * @param {string|Object} condition - Either a pattern name or `{name, uuid}`.
 * @param {Array<Object>} nutritionPatterns - Loaded patterns from the store.
 * @returns {Object|undefined} The matching pattern, or undefined if not found.
 */
export function resolveConditionPattern(condition, nutritionPatterns) {
    if (!condition || !Array.isArray(nutritionPatterns)) {
        return undefined;
    }

    const name = typeof condition === 'string' ? condition : condition.name;
    const uuid = typeof condition === 'object' ? condition.uuid : undefined;

    return nutritionPatterns.find((pattern) => {
        if (uuid && pattern.uuid === uuid) {
            return true;
        }
        return name && pattern.name === name;
    });
}
